import * as API from './api.js';
import { JOB_PHASES, PROGRESS_PHASES } from './config/jobPhases.js';
import { UI_COPY } from './config/uiCopy.js';

let isCancelled = false;
let currentJobId = null;
let lastPhase = null;
const firstProgressSeenForJob = new Set();

export function emitJobEvent(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
}

export function setCancelled(value) {
    isCancelled = value;
}

export function isJobCancelled() {
    return isCancelled;
}

export function setCurrentJobId(jobId) {
    currentJobId = jobId;
    if (jobId) {
        firstProgressSeenForJob.delete(String(jobId));
    }
}

export function getCurrentJobId() {
    return currentJobId;
}

export function resetPhase() {
    lastPhase = null;
}

export function clearJobState() {
    currentJobId = null;
    lastPhase = null;
    firstProgressSeenForJob.clear();
}

function computePct(current, total) {
    if (!total) return 0;
    const rawPct = (Number(current || 0) / Number(total || 0)) * 100;
    const clamped = Math.max(0, Math.min(100, rawPct));
    if (clamped > 0 && clamped < 0.1) return 0.1;
    return Math.round(clamped * 10) / 10;
}

export async function handleComplete(jobId) {
    try {
        const fullStatus = await API.getStatus(jobId);
        const manifest = await API.getManifest(jobId);
        const jobConfig = manifest.settings || manifest.meta || manifest;

        emitJobEvent('job:complete', {
            jobId,
            image_url: fullStatus.image_url,
            preview_url: fullStatus.preview_url,
            inspect_url: fullStatus.inspect_url,
            source_url: fullStatus.source_url || null,
            manifest,
            settings: jobConfig || null,
            tileMap: manifest.map || null,
            trivia: manifest.trivia || null
        });
    } catch (e) {
        console.error('Failed to load final job data:', e);
        emitJobEvent('job:error', {
            jobId,
            message: UI_COPY.jobStatus.resultLoadFailed,
            error: String(e)
        });
    }
}

export function handleErrorOrCancelled(jobId, statusData) {
    const phase = statusData.phase;
    const message = statusData.message || (phase === JOB_PHASES.CANCELLED ? UI_COPY.jobStatus.cancelledFallback : UI_COPY.jobStatus.errorFallback);

    if (phase === JOB_PHASES.CANCELLED) {
        emitJobEvent('job:cancelled', { jobId, message, raw: statusData });
    } else {
        emitJobEvent('job:error', { jobId, message, raw: statusData });
    }
}

export function handleProgress(jobId, statusData, options = {}) {
    const phase = statusData.phase;
    const suppressBuildToast = Boolean(options && options.suppressBuildToast);
    if (phase && phase !== lastPhase) {
        lastPhase = phase;
        emitJobEvent('job:phase', { jobId, phase, raw: statusData });
    }

    if (PROGRESS_PHASES.has(phase)) {
        const safeJobId = String(jobId || '');
        if (safeJobId && !firstProgressSeenForJob.has(safeJobId)) {
            firstProgressSeenForJob.add(safeJobId);
            emitJobEvent('job:first_progress', {
                jobId: safeJobId,
                phase,
                raw: statusData,
            });
        }
        let pct = computePct(statusData.current, statusData.total);
        if (
            pct === 0 &&
            (phase === JOB_PHASES.PLANNING || phase === JOB_PHASES.RENDERING || phase === JOB_PHASES.BUILDING)
        ) {
            pct = 1;
        }
        emitJobEvent('job:progress', {
            jobId,
            phase,
            pct,
            current: statusData.current ?? null,
            total: statusData.total ?? null,
            file: statusData.file ?? null,
            livePreviewUrl: statusData.live_preview_url ?? null,
            livePreviewVersion: statusData.live_preview_version ?? null,
            livePreviewKey: statusData.live_preview_key ?? null,
            livePreviewPct: statusData.live_preview_pct ?? null,
            livePreviewPhase: statusData.live_preview_phase ?? null,
            raw: statusData,
            suppressBuildToast
        });
    }
}
